import { CompanySettings, InvoiceSpecificationType } from "@elton/types";
import React, { ReactNode, useState } from "react";
import { Card, CardContent, CardHeader } from "@ui/components/ui/card.tsx";
import { T } from "@transifex/react";
import { useUpdateCompanySettings } from "@/api/mutations/company-settings/update-company-settings.ts";
import { LoadingButton } from "@/components/buttons/loading-button.tsx";
import { Alert, AlertDescription } from "@ui/components/ui/alert.tsx";
import PostHogAdapter from "@/adapters/posthog-adapter.ts";

interface IProps {
  data: {
    settings: CompanySettings;
    companyId: string;
  };
}

function RadioButtonInput(props: {
  invoiceSpecification: InvoiceSpecificationType;
  invoiceSpecificationOption: InvoiceSpecificationType;
  labelText: ReactNode;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center">
      <input
        id="radio-enabled"
        name="feature-toggle"
        type="radio"
        value="enabled"
        className="h-4 w-4 border-gray-300 text-teal-600"
        checked={props.invoiceSpecification === props.invoiceSpecificationOption}
        onChange={props.onChange}
      />
      <label htmlFor="radio-enabled" className="ml-2 block text-sm font-medium text-current">
        {props.labelText}
      </label>
    </div>
  );
}

export const InvoiceSpecificationCard: React.FC<IProps> = ({ data }) => {
  const [invoiceSpecification, setInvoiceSpecification] = useState<InvoiceSpecificationType>(
    data.settings.invoiceSpecification,
  );

  const updateCompanySettings = useUpdateCompanySettings(data.companyId);

  return (
    <div>
      <Card>
        <CardHeader>
          <h3 className="text-md font-medium">
            <T _str="Customize Invoice" />
          </h3>
          <p className="text-sm text-muted-foreground text-pretty">
            <T _str="Choose how invoice lines are grouped." />
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-current">
            <RadioButtonInput
              invoiceSpecification={invoiceSpecification}
              invoiceSpecificationOption={InvoiceSpecificationType.NO_SPECIFICATION}
              labelText={<T _str="Charging total amount" />}
              onChange={() => setInvoiceSpecification(InvoiceSpecificationType.NO_SPECIFICATION)}
            />
            <RadioButtonInput
              invoiceSpecification={invoiceSpecification}
              invoiceSpecificationOption={
                InvoiceSpecificationType.SPLIT_ON_VEHICLE_REGISTRATION_NUMBER
              }
              labelText={<T _str="Charging amount grouped by registration number" />}
              onChange={() =>
                setInvoiceSpecification(
                  InvoiceSpecificationType.SPLIT_ON_VEHICLE_REGISTRATION_NUMBER,
                )
              }
            />
            <RadioButtonInput
              invoiceSpecification={invoiceSpecification}
              invoiceSpecificationOption={InvoiceSpecificationType.SPLIT_ON_EMPLOYEE}
              labelText={<T _str="Charging amount grouped by employee" />}
              onChange={() => setInvoiceSpecification(InvoiceSpecificationType.SPLIT_ON_EMPLOYEE)}
            />
          </div>
          <div className="max-w-fit mt-4 ">
            {invoiceSpecification == InvoiceSpecificationType.SPLIT_ON_EMPLOYEE && (
              <Alert>
                <AlertDescription>
                  <T _str="Tip: Add employee names to the portal. If missing, phone numbers will appear on invoices instead." />
                </AlertDescription>
              </Alert>
            )}
            {invoiceSpecification ==
              InvoiceSpecificationType.SPLIT_ON_VEHICLE_REGISTRATION_NUMBER && (
              <Alert>
                <AlertDescription>
                  <T _str="Tip: Assign a standard vehicle registration number to employees in the portal. Employees can also add any registration number for each charging session" />
                </AlertDescription>
              </Alert>
            )}
          </div>
          <div className="flex w-full justify-end mt-4">
            <LoadingButton
              disabled={data.settings.invoiceSpecification === invoiceSpecification}
              onClick={() => {
                updateCompanySettings.mutateAsync({
                  noteField: data.settings.noteField,
                  regNumberField: data.settings.regNumberField,
                  invoiceSpecification: invoiceSpecification,
                });
                PostHogAdapter.logEvent("invoice_specification_changed", {
                  invoiceSpecification: invoiceSpecification,
                });
              }}
              loading={updateCompanySettings.isPending}
            >
              <T _str="Save" />
            </LoadingButton>
          </div>
        </CardContent>
      </Card>
      <p className="text-sm text-muted-foreground text-pretty mt-3">
        <T _str="Do you want to change how every invoice is labelled? Contact us at support@elton.app." />
      </p>
    </div>
  );
};
